

import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { authApi } from '@/lib/api/auth.api';
import { useLoginSession } from '@/store/login-session.store';

// Import shadcn components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export default function ResendCodePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAddAccountFlow = searchParams.has('addAccount');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const setLoginSession = useLoginSession(state => state.setLoginSession);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const normalizedEmail = normalizeEmail(email);
    
    if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setIsLoading(true);
      
      // Attempt to resend the confirmation code
      // The backend will handle checking if the email exists and is unconfirmed
      const result = await authApi.resendCode(normalizedEmail);
      
      if (result.success) {
        setIsSuccess(true);
        toast.success('Verification code sent! Check your email.');
        
        // Set the email in the auth store
        setLoginSession(normalizedEmail, '');
        
        // Redirect to confirmation page after a short delay
        setTimeout(() => {
          const confirmationUrl = `/register/confirmation?email=${encodeURIComponent(normalizedEmail)}${isAddAccountFlow ? '&addAccount=true' : ''}`;
          navigate(confirmationUrl);
        }, 1500);
      } else {
        throw new Error(result.message || 'Failed to send verification code');
      }
    } catch (error) {
      if (error instanceof Error) {
        // If the error indicates the user is already confirmed
        if (error.message.toLowerCase().includes('already confirmed') ||
            error.message.toLowerCase().includes('verified')) {
          toast.info('Your account is already confirmed. Please login instead.');
          setTimeout(() => {
            const loginUrl = `/login?email=${encodeURIComponent(normalizedEmail)}${isAddAccountFlow ? '&addAccount=true' : ''}`;
            navigate(loginUrl);
          }, 1500);
        } else if (error.message.toLowerCase().includes('not found') || 
                  error.message.toLowerCase().includes('not registered')) {
          // If the error indicates the email doesn't exist
          toast.error('This email is not registered. Please sign up first.');
        } else {
          toast.error(error.message);
        }
      } else {
        toast.error('An unexpected error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="w-full mt-6">
        <div className="mb-6 text-center">
          <h1 className="text-4xl font-light tracking-tight text-center mb-2 text-foreground">Resend Verification Code</h1>
          <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
            Enter your email address to receive a new verification code
          </p>
        </div>

        {isSuccess ? (
          <Alert variant="default" className="border-primary/30 bg-primary/5 text-primary mb-6">
            <AlertDescription>
              <p className="font-medium">Verification code sent!</p>
              <p className="mt-1">Please check your email for the verification code. Redirecting to verification page...</p>
            </AlertDescription>
          </Alert>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(normalizeEmail(e.target.value))}
                  className="pl-10"
                  disabled={isLoading}
                  required
                />
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                  </svg>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Enter the email you used during registration
              </p>
            </div>

            <Button 
              type="submit"
              className="w-full"
              disabled={isLoading || !email}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 animate-spin">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                  </svg>
                  Sending...
                </span>
              ) : (
                <>
                  Resend Code
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-2 h-4 w-4 inline">
                    <path d="M5 12h14"></path>
                    <path d="m12 5 7 7-7 7"></path>
                  </svg>
                </>
              )}
            </Button>
          </form>
        )}

        <div className="mt-8 text-center">
          <Button
            type="button"
            variant="ghost"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => navigate(isAddAccountFlow ? '/register?addAccount=true' : '/register')}
            disabled={isLoading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1.5 h-3.5 w-3.5 inline">
              <path d="m12 19-7-7 7-7"></path>
              <path d="M19 12H5"></path>
            </svg>
            Back to Registration
          </Button>
        </div>
      </div>
    </AuthLayout>
  );
} 
